import { triggerPriceTypeEnum, type TriggerPriceType } from '@flipster/apis/v2/trade/types';
import Num from '@flipster/utils/num';

import type { Side } from './bunja/validation';

export type TpSlExecutionType = 'market' | 'limit';
export type LegType = 'tp' | 'sl';

/** BBO(best counterparty) price-match strategy sent to the API for a Limit leg. */
export const BBO_PRICE_MATCH_STRATEGY = 'PRICE_MATCH_STRATEGY_COUNTERPARTY_1';

export interface TpSlLeg {
  triggerPrice: string;
  triggerPriceType: TriggerPriceType;
  executionType: TpSlExecutionType;
  /** meaningful only when executionType === 'limit' && !isBbo */
  limitPrice: string;
  /** meaningful only when executionType === 'limit' */
  isBbo: boolean;
}

export interface AdvancedTpSlPreset {
  side: Extract<Side, 'Long' | 'Short'>;
  tp: TpSlLeg;
  sl: TpSlLeg;
}

export const emptyLeg = (): TpSlLeg => ({
  triggerPrice: '',
  triggerPriceType: triggerPriceTypeEnum.last,
  executionType: 'market',
  limitPrice: '',
  isBbo: false,
});

export const emptyPreset = (side: AdvancedTpSlPreset['side']): AdvancedTpSlPreset => ({
  side,
  tp: emptyLeg(),
  sl: emptyLeg(),
});

/**
 * 기본(inline) TP/SL 입력값을 Advanced preset 초안으로 매핑한다.
 * inline 입력은 trigger price만 가지므로 두 leg 모두 market 실행으로 둔다.
 * Advanced 모달을 처음 열 때 직전 inline 입력값을 그대로 prefill하는 데 사용한다.
 */
export const presetFromBasicTpSl = (
  side: AdvancedTpSlPreset['side'],
  basic: {
    tp?: string;
    sl?: string;
    tpTriggerPriceType?: TriggerPriceType;
    slTriggerPriceType?: TriggerPriceType;
  },
): AdvancedTpSlPreset => ({
  side,
  tp: {
    ...emptyLeg(),
    triggerPrice: basic.tp ?? '',
    triggerPriceType: basic.tpTriggerPriceType ?? triggerPriceTypeEnum.last,
  },
  sl: {
    ...emptyLeg(),
    triggerPrice: basic.sl ?? '',
    triggerPriceType: basic.slTriggerPriceType ?? triggerPriceTypeEnum.last,
  },
});

/** leg가 활성(주문에 포함)인지 여부 — 별도 enable 체크박스 없이 trigger price 입력값으로 판단한다. */
export const isLegActive = (leg: TpSlLeg): boolean => Num(leg.triggerPrice).isGreaterThan(0);

/**
 * Trigger price direction vs the entry anchor.
 * Long  → TP trigger > anchor, SL trigger < anchor
 * Short → TP trigger < anchor, SL trigger > anchor
 */
export const validateTriggerDirection = ({
  side,
  leg,
  triggerPrice,
  anchor,
}: {
  side: AdvancedTpSlPreset['side'];
  leg: LegType;
  triggerPrice: string;
  anchor: string;
}): boolean => {
  const trigger = Num(triggerPrice);
  const base = Num(anchor);
  if (!trigger.isGreaterThan(0) || !base.isGreaterThan(0)) return false;
  const triggerAboveAnchor = (side === 'Long') === (leg === 'tp');
  return triggerAboveAnchor ? trigger.isGreaterThan(base) : trigger.isLessThan(base);
};

/** An active leg is valid when its trigger direction is correct and, for a fixed Limit, a limit price is present. */
export const isLegValid = (
  leg: TpSlLeg,
  { side, legType, anchor }: { side: AdvancedTpSlPreset['side']; legType: LegType; anchor: string },
): boolean => {
  if (!isLegActive(leg)) return true; // trigger price 미입력 leg는 무시 → Confirm 막지 않음
  if (!validateTriggerDirection({ side, leg: legType, triggerPrice: leg.triggerPrice, anchor })) return false;
  if (leg.executionType === 'limit' && !leg.isBbo) {
    return Num(leg.limitPrice).isGreaterThan(0);
  }
  return true;
};

/** Confirm is disabled when no leg has a trigger price, or any active leg is invalid. */
export const isConfirmDisabled = (preset: AdvancedTpSlPreset, { anchor }: { anchor: string }): boolean => {
  if (!isLegActive(preset.tp) && !isLegActive(preset.sl)) return true;
  return (
    !isLegValid(preset.tp, { side: preset.side, legType: 'tp', anchor }) ||
    !isLegValid(preset.sl, { side: preset.side, legType: 'sl', anchor })
  );
};

/** Returns the preset only when its side matches the submit side and ≥1 leg has a trigger price. */
export const selectActivePreset = ({
  submitSide,
  preset,
}: {
  submitSide: Side;
  preset: AdvancedTpSlPreset | null;
}): AdvancedTpSlPreset | null => {
  if (!preset) return null;
  if (preset.side !== submitSide) return null;
  if (!isLegActive(preset.tp) && !isLegActive(preset.sl)) return null;
  return preset;
};

export interface TpSlExecutionFields {
  orderTpTriggerPrice?: string;
  orderTpTriggerPriceType?: TriggerPriceType;
  orderTpOrderType?: 'Market' | 'Limit';
  orderTpLimitPrice?: string | null;
  orderTpPriceMatchStrategy?: string;
  orderSlTriggerPrice?: string;
  orderSlTriggerPriceType?: TriggerPriceType;
  orderSlOrderType?: 'Market' | 'Limit';
  orderSlLimitPrice?: string | null;
  orderSlPriceMatchStrategy?: string;
}

const legToFields = (leg: TpSlLeg, prefix: 'orderTp' | 'orderSl'): TpSlExecutionFields => {
  if (!isLegActive(leg)) return {};
  const fields: Record<string, unknown> = {
    [`${prefix}TriggerPrice`]: leg.triggerPrice,
    // ROLLBACK(last-price): triggerPriceType 전송 재활성화 시 복원.
    // [`${prefix}TriggerPriceType`]: leg.triggerPriceType,
    [`${prefix}OrderType`]: leg.executionType === 'limit' ? 'Limit' : 'Market',
  };
  if (leg.executionType === 'limit') {
    if (leg.isBbo) {
      fields[`${prefix}LimitPrice`] = null;
      fields[`${prefix}PriceMatchStrategy`] = BBO_PRICE_MATCH_STRATEGY;
    } else {
      fields[`${prefix}LimitPrice`] = leg.limitPrice;
    }
  }
  return fields as TpSlExecutionFields;
};

/** Maps a preset's enabled legs to the order-create TP/SL execution payload fields. */
export const buildTpSlExecutionFields = (preset: AdvancedTpSlPreset): TpSlExecutionFields => ({
  ...legToFields(preset.tp, 'orderTp'),
  ...legToFields(preset.sl, 'orderSl'),
});

/** Inline(간편) TP/SL 입력 — trigger price만 갖는다. */
export interface BasicTpSlInput {
  enabled: boolean;
  tp?: string;
  sl?: string;
  tpTriggerPriceType?: TriggerPriceType;
  slTriggerPriceType?: TriggerPriceType;
}

/**
 * 주문 제출 시 전송할 TP/SL 실행 payload를 결정한다. (제출 경로 wiring 로직)
 * 1. Advanced preset이 설정돼 있으면(leg 입력 있음) 그 preset이 TP/SL을 전담한다.
 *    - 제출 방향과 side가 같으면 실행 필드(Limit/BBO 포함)를 전송한다.
 *    - side가 다르면 TP/SL을 전혀 전송하지 않는다 (인라인으로 폴백하지 않음).
 * 2. Advanced 미설정이면 인라인 간편 TP/SL(Market trigger price)을 사용한다.
 * 3. 인라인도 비활성(enabled=false)이면 빈 payload(TP/SL 없음).
 */
export const resolveTpSlExecutionFields = ({
  submitSide,
  advancedPreset,
  basic,
}: {
  submitSide: Side;
  advancedPreset: AdvancedTpSlPreset | null;
  basic: BasicTpSlInput;
}): TpSlExecutionFields => {
  // leg가 하나라도 입력된 preset은 "설정됨"으로 보고, 그때부터 TP/SL은 preset이 전담한다.
  const isPresetConfigured = !!advancedPreset && (isLegActive(advancedPreset.tp) || isLegActive(advancedPreset.sl));
  if (isPresetConfigured) {
    // 제출 방향과 같으면 적용, side 불일치면 인라인 폴백 없이 빈 payload — 설정한 방향의 주문에만 적용한다.
    const activePreset = selectActivePreset({ submitSide, preset: advancedPreset });
    return activePreset ? buildTpSlExecutionFields(activePreset) : {};
  }
  if (!basic.enabled) return {};
  const fields: TpSlExecutionFields = {};
  if (basic.tp) {
    fields.orderTpTriggerPrice = basic.tp;
    // ROLLBACK(last-price): triggerPriceType 전송 재활성화 시 복원.
    // fields.orderTpTriggerPriceType = basic.tpTriggerPriceType ?? triggerPriceTypeEnum.last;
  }
  if (basic.sl) {
    fields.orderSlTriggerPrice = basic.sl;
    // fields.orderSlTriggerPriceType = basic.slTriggerPriceType ?? triggerPriceTypeEnum.last;
  }
  return fields;
};

export type SlLiquidationError = {
  messageKey: 'Price_above_liquidation_error' | 'Price_below_liquidation_error';
  liquidationPrice: string;
};

/**
 * 주문 폼에서 설정한 SL(Stop Loss) 가격이 예상 청산가를 침범하는지 검사한다.
 *
 * - Long: SL은 청산가보다 낮을 수 없다. (SL < 청산가 → "청산가보다 높게 입력" 에러)
 * - Short: SL은 청산가보다 높을 수 없다. (SL > 청산가 → "청산가보다 낮게 입력" 에러)
 *
 * 비교 기준은 prex-core 청산 엔진과 일치하는 주문 폼의 post-fill 청산가 프리뷰다.
 * SL 미설정이거나 청산가 추정이 불가능한(null) 경우에는 검사하지 않는다.
 */
export function getSlLiquidationError(params: {
  side: Side;
  slPrice: string | undefined;
  liqPrice: string | null;
}): SlLiquidationError | null {
  const { side, slPrice, liqPrice } = params;
  if (!slPrice || !liqPrice) return null;

  if (side === 'Long' && Num(slPrice).isLessThan(liqPrice)) {
    return { messageKey: 'Price_above_liquidation_error', liquidationPrice: liqPrice };
  }
  if (side === 'Short' && Num(slPrice).isGreaterThan(liqPrice)) {
    return { messageKey: 'Price_below_liquidation_error', liquidationPrice: liqPrice };
  }
  return null;
}

/**
 * 포지션 모드(One-way ↔ Multi-Position)가 전환됐을 때 설정해 둔 TP/SL을 초기화해야 하는지 판단한다. (QA-20888)
 *
 * 모드 전환은 청산가·주문 동작 전반을 바꾸므로 재입력을 유도한다. 값이 실제로 바뀐 경우(One-way↔Hedge)에만
 * 초기화하고, 동일하면 초기화하지 않는다.
 *
 * 참고: 레버리지·마진 모드 변경은 (거래소마다 동작이 제각각이고 사용자 편의상) TP/SL을 초기화하지 않으므로
 * 별도 판정 함수를 두지 않는다 — 포지션 모드 전환만 초기화 대상이다.
 */
export const shouldResetTpSlOnPositionModeChange = ({
  prevIsOneWay,
  nextIsOneWay,
}: {
  prevIsOneWay: boolean;
  nextIsOneWay: boolean;
}): boolean => prevIsOneWay !== nextIsOneWay;
